"use client";

import { buildId } from "@faire/slate/lib/idUtils";
import { componentGroupCls } from "@faire/slate/lib/tracking";
import { SELECTION_CARD_GROUP_UNIQUE_NAME_PREFIX } from "@faire/slate/private/selection-card/consts";
import { useControllableRadioGroupState } from "@faire/slate/private/selection-card/hooks/useControllableRadioGroupState";
import React from "react";

import { SelectionCard } from "./SelectionCard";

type IProps = {
  /**
   * The cards to display in the group.
   */
  items: Array<React.ComponentProps<typeof SelectionCard>>;

  /**
   * **web**
   * The selection card group data-test-id prop.
   */
  "data-test-id"?: string;

  /**
   * **web**
   * The name of the group. This is important to make the group to work as a radio group.
   */
  name?: string;

  /**
   * **web**
   * The ref of the selection card group.
   */
  ref?: React.Ref<HTMLDivElement>;

  // DO NOT ADD any new prop without aligning with Slate team first
} & React.AriaAttributes;

/**
 * SelectionCardGroup enables users to select one SelectionCard from a list.
 *
 * {@link https://www.notion.so/faire/Selection-Card-2242efb5c25a8048a616c00d27292b19 | Slate/SelectionCardGroup}
 */
export const SelectionCardGroup: React.FC<IProps> = ({
  items,
  name,
  ...props
}) => {
  const groupName =
    name ??
    buildId(SELECTION_CARD_GROUP_UNIQUE_NAME_PREFIX, props["aria-label"]);
  const [checked, onChange] = useControllableRadioGroupState({
    checked: items.map(({ checked }) => checked),
    onChange: items.map(({ onChange }) => onChange),
  });
  return (
    <div
      {...props}
      className={componentGroupCls("fs-selection-card-group")}
      data-component-name="Slate/SelectionCardGroup"
      role="radiogroup"
    >
      {items.map((item, i) => (
        <SelectionCard
          key={`${item.label}-${i}`}
          {...item}
          checked={checked[i]}
          onChange={onChange[i]}
          name={groupName}
        />
      ))}
    </div>
  );
};
